import type { ReactNode } from 'react'
import { match } from '@/utils/pattern-match-util'
import {
  CardHeader,
  CardTitle,
  CardContent,
  CardContentDescription,
} from '@/components/Card'
import { ProgressBar } from '@/components/ProgressBar'
import { Button } from '@/components/Button'
import { dateFormatters } from '@/utils/date-util'
import styles from './ConsultationCallCard.module.css'

export interface ConsultationProgressBarProps {
  completed: number
  pending: number
}

export function ConsultationProgressBar({
  completed,
  pending,
}: ConsultationProgressBarProps) {
  return (
    <ProgressBar
      bars={[
        {
          progress: completed,
        },
        {
          progress: pending,
          style: 'striped',
        },
      ]}
    />
  )
}

export interface CompleteConsultationJobButtonProps {
  onPress: () => void
}

export function CompleteConsultationJobButton({
  onPress,
}: CompleteConsultationJobButtonProps) {
  return (
    <Button
      label="Complete Job"
      variant="secondary"
      size="wide-slim"
      onPress={onPress}
      testId="consultation-complete-job-button"
    />
  )
}

export interface PendingSchedulingContentProps {
  isPendingBooking: boolean
  bookingModal: ReactNode
  previouslyBooked: boolean | undefined
  onSelectAppointment: () => void
  isSelectAppointmentPending: boolean
  pendingSchedulingText: ReactNode
}

export function PendingSchedulingContent({
  isPendingBooking,
  bookingModal,
  previouslyBooked,
  onSelectAppointment,
  isSelectAppointmentPending,
  pendingSchedulingText,
}: PendingSchedulingContentProps) {
  return isPendingBooking ? (
    <>
      {bookingModal}
      <CardHeader>
        <CardTitle testId="current-task-title">
          {previouslyBooked ? 'Rescheduling requested' : 'Call scheduled'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar bars={[{ progress: 100, style: 'striped' }]} />
        <br />
        <CardContentDescription>
          <p>
            Your client has{' '}
            {previouslyBooked ? 'requested to reschedule' : 'scheduled a call'}.
            Please review and select a new date and time to continue.
          </p>
        </CardContentDescription>
      </CardContent>
      <CardContent>
        <Button
          label={
            previouslyBooked ? 'Select new appointment' : 'Select appointment'
          }
          variant="primary"
          size={'wide-slim'}
          onPress={onSelectAppointment}
          isDisabled={isSelectAppointmentPending}
          isPending={isSelectAppointmentPending}
          testId={'book-consultation-button'}
        />
      </CardContent>
    </>
  ) : (
    <>
      <CardHeader>
        <CardTitle testId="current-task-title">
          Prepare for the consultation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <CardContentDescription>
          <p>{pendingSchedulingText}</p>
        </CardContentDescription>
      </CardContent>
    </>
  )
}

export interface DownloadCalendarButtonProps {
  isPending: boolean
  onPress: () => void
}

export function DownloadCalendarButton({
  isPending,
  onPress,
}: DownloadCalendarButtonProps) {
  return (
    <Button
      label="Add To Calendar"
      variant="secondary"
      size={'wide-slim'}
      isDisabled={isPending}
      isPending={isPending}
      onPress={onPress}
      testId="consultation-download-calendar-button"
    />
  )
}

export type RoomCardCallStep =
  'Unknown' | 'Pending' | 'InProgress' | 'Done' | 'Completed'

export interface RoomCardTitleProps {
  skipScheduling: boolean
  callStep: RoomCardCallStep
}

export function RoomCardTitle({
  skipScheduling,
  callStep,
}: RoomCardTitleProps) {
  if (skipScheduling) {
    return <>Prepare for the consultation</>
  }

  return match(callStep, {
    Pending: () => <>Prepare for the consultation</>,
    InProgress: () => <>Consultation in progress</>,
    Done: () => <>Finalize the consultation</>,
    Completed: () => <>Job Completed</>,
    Unknown: () => undefined,
  })
}

export interface RoomCardDescriptionProps {
  startTime: Date | undefined
  skipScheduling: boolean
  callStep: RoomCardCallStep
}

export function RoomCardDescription({
  skipScheduling,
  callStep,
  startTime,
}: RoomCardDescriptionProps) {
  if (skipScheduling) {
    return (
      <>
        Use the chat to arrange a date and time for the call with your client.
        Once a time has been agreed on, please join the call.
      </>
    )
  }

  return match(callStep, {
    Pending: () =>
      startTime ? (
        <>
          <p>
            Call scheduled with client on{' '}
            <span className={styles.important}>
              {` ${dateFormatters.dateTimeWithZone.format(startTime)}. `}
            </span>
          </p>
        </>
      ) : undefined,
    InProgress: () => <>The consultation is currently in progress.</>,
    Done: () => (
      <>Start another call with the client, or proceed to complete the job.</>
    ),
    Completed: () => (
      <>The consultation has been completed and the room has been closed.</>
    ),
    Unknown: () => undefined,
  })
}
